import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap"
import { Form } from "react-bootstrap"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router-dom"
import { UncontrolledTooltip } from "reactstrap"
import toastr from "toastr"
import TableContainer from "../../components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import apiURl from "url"

const MonthlyReport = () => {
  document.title = "Monthly Report"
  const role = localStorage.getItem("role")

  const navigate = useNavigate()

  const [reports, setReports] = useState([])
  const [filteredReports, setFilteredReports] = useState([])
  const [pdfModal, setPdfModal] = useState(false)
  const [pdfUrl, setPdfUrl] = useState("")
  const [email, setEmail] = useState("")
  const [availableBusiness, setAvailableBusiness] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" })

  // Fetch all clients
  const fetchAllClients = async () => {
    try {
      const response = await fetch(`${apiURl}/api/clients/fetchClients`)
      const json = await response.json()
      if (response.status === 200) {
        setAvailableBusiness(
          json.clients.map(user => ({
            value: user.id,
            label: user.business_name,
          }))
        )
      }
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch clients")
    }
  }

  // Fetch all monthly reports with client data
  const fetchAllMonthlyReports = async () => {
    try {
      let url = `${apiURl}/api/Monthlyreports/fetchMonthlyReports`

      // Only append client_id for Client role
      if (role === "Client") {
        const ClientID = localStorage.getItem("login_user_id")
        url += `?client_id=${ClientID}`
      }

      const response = await fetch(url)
      const json = await response.json()
      if (response.status === 200) {
        console.log(json.reports)
        setReports(json.reports)
        setFilteredReports(json.reports.reverse())
      }
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch reports")
    }
  }

  // Apply client-side filters
  useEffect(() => {
    let filtered = [...reports]

    // Filter by client
    if (selectedClient) {
      filtered = filtered.filter(
        report => report.client_id === selectedClient.value
      )
    }

    // Filter by date range
    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(report => {
        const reportDate = new Date(report.reportDate)
        const start = dateRange.startDate ? new Date(dateRange.startDate) : null
        const end = dateRange.endDate ? new Date(dateRange.endDate) : null
        return (!start || reportDate >= start) && (!end || reportDate <= end)
      })
    }

    setFilteredReports(filtered.reverse())
  }, [reports, selectedClient, dateRange])

  useEffect(() => {
    fetchAllClients()
    fetchAllMonthlyReports()
  }, [])

  // Handle send email
  const handleSendEmail = async () => {
    if (!email.trim()) {
      toastr.error("Please enter an email address")
      return
    }
    try {
      const response = await fetch(`${apiURl}/api/Monthlyreports/sendReport`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, pdfUrl }),
      })
      const json = await response.json()
      if (response.ok) {
        toastr.success("Email sent successfully")
        setEmail("")
        setPdfModal(false)
      } else {
        toastr.error(json.message || "Failed to send email")
      }
    } catch (err) {
      console.error("Send Email Error:", err)
      toastr.error("Server error while sending email")
    }
  }

  // Reset filters
  const handleResetFilters = () => {
    setSelectedClient(null)
    setDateRange({ startDate: "", endDate: "" })
  }

  // Handle date change with validation
  const handleDateChange = (field, value) => {
    setDateRange(prev => {
      const newRange = { ...prev, [field]: value }
      if (newRange.startDate && newRange.endDate) {
        if (new Date(newRange.startDate) > new Date(newRange.endDate)) {
          toastr.error("Start date cannot be after end date")
          return prev
        }
      }
      return newRange
    })
  }

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Business Name",
        accessor: "client.business_name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Phone",
        accessor: "client.phone",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Email",
        accessor: "client.email",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Report Date",
        accessor: "reportDate",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Action",
        Cell: ({ row }) => {
          const id = row.original.id // ensure id is defined here
          const url = encodeURI(row.original.pdfPath)
          return (
            <>
              <Button
                color="primary"
                size="sm"
                onClick={() => {
                  setPdfUrl(url)
                  setPdfModal(true)
                }}
                className="me-2"
              >
                View
              </Button>
              {role === "Admin" && (
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => {
                    deleteMReport(id)
                  }}
                >
                  Delete
                </Button>
              )}
            </>
          )
        },
      },
    ],
    []
  )

  //Delete Modal
  const [deleteModal, setDeleteModal] = useState(false)
  const [del_id, setDel_id] = useState([])
  const deleteMReport = id => {
    setDel_id(id)
    setDeleteModal(true)
  }

  //Delete Parcel
  // Delete Report
  const handleDeleteReport = async () => {
    const data = {
      del_id: del_id,
    }

    try {
      const response = await fetch(
        `${apiURl}/api/Monthlyreports/deleteMReport`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      )

      const result = await response.json()

      if (response.status === 200) {
        fetchAllMonthlyReports()
        setDeleteModal(false)
        toastr.success("Report Deleted Successfully")
      } else {
        setDeleteModal(false)
        toastr.error(result.message || "Failed to Delete")
      }
    } catch (error) {
      console.error("Delete Error:", error)
      toastr.error("Failed to Delete")
    }
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteReport}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">
                <b>Monthly Reports</b>
              </h5>
            </Col>
            {role !== "Client" && (
              <Col>
                <div className="float-end">
                  <Button
                    color="primary"
                    className="btn-sm w-md"
                    onClick={() => navigate("/add-monthly-report")}
                  >
                    <i className="fa fa-fw fa-plus me-1" />
                    Create Report
                  </Button>
                </div>
              </Col>
            )}
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    {role !== "Client" && (
                      <Col md="4">
                        <FormGroup>
                          <Label for="clientFilter">Filter by Client</Label>
                          <select
                            id="clientFilter"
                            className="form-select"
                            value={selectedClient?.value || ""}
                            onChange={e => {
                              const client = availableBusiness.find(
                                b => b.value === parseInt(e.target.value)
                              )
                              setSelectedClient(client || null)
                            }}
                          >
                            <option value="">All Clients</option>
                            {availableBusiness.map(client => (
                              <option key={client.value} value={client.value}>
                                {client.label}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </Col>
                    )}

                    <Col md="3">
                      <FormGroup>
                        <Label for="startDate">Start Date</Label>
                        <Input
                          type="date"
                          id="startDate"
                          value={dateRange.startDate}
                          onChange={e =>
                            handleDateChange("startDate", e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="endDate">End Date</Label>
                        <Input
                          type="date"
                          id="endDate"
                          value={dateRange.endDate}
                          onChange={e =>
                            handleDateChange("endDate", e.target.value)
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" className="d-flex align-items-end">
                      <FormGroup>
                        <Button
                          color="danger"
                          className="w-100"
                          onClick={handleResetFilters}
                        >
                          Reset Filters
                        </Button>
                      </FormGroup>
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={filteredReports}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    className="custom-table"
                    customPageSize={10}
                    FilterColumns={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* PDF View Modal */}
      <Modal
        isOpen={pdfModal}
        toggle={() => setPdfModal(!pdfModal)}
        size="xl"
        centered
      >
        <ModalHeader toggle={() => setPdfModal(!pdfModal)}>
          View Generated Report
        </ModalHeader>
        <ModalBody>
          <iframe
            src={pdfUrl}
            style={{ width: "100%", height: "450px", border: "none" }}
            title="PDF Report"
          />
          {role !== "Client" && (
            <FormGroup className="mt-3">
              <Label for="emailInput">Send Report to Email</Label>
              <Input
                type="email"
                id="emailInput"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter recipient email"
              />
            </FormGroup>
          )}
        </ModalBody>
        {role !== "Client" && (
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setPdfModal(false)
                setEmail("")
                setPdfUrl("")
              }}
            >
              Close
            </Button>
            <Button
              color="primary"
              onClick={handleSendEmail}
              disabled={!email.trim()}
            >
              Send Email
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </React.Fragment>
  )
}

export default MonthlyReport
